import { useCallback, useEffect, useMemo, useState } from "react";
import { findFirstComponentOfType, TextExtractor } from "../internal/TextExtractor";
import { useCurrentSearchHitIndex, useGoToHit, useSearchHits } from "../../state/SearchContext";

interface SetCurrentSearchHitButtonProps {
  index: string,
  label?: string,
  mode?: "relative" | "absolute",
  children: React.ReactNode,
  component?: "button" | "span" | "div",
  className?: string,
}

/***
 * A button that sets the current search hit to the given index.
 * The button label can either be supplied in the label property or as content of the SetCurrentSearchHitButton
 * element.
 */
export function SetCurrentSearchHitButton({ index, label, children, mode = "relative", component = "div", className }: SetCurrentSearchHitButtonProps): React.ReactElement {
  const currentSearchHitIndex = useCurrentSearchHitIndex();
  const goToHit = useGoToHit();
  const searchHits = useSearchHits();

  // Get the label from the label child
  const labelChild = useMemo(() => findFirstComponentOfType(children, "arg-label"), [children]);

  // Get the index from the index child
  const indexChild = useMemo(() => findFirstComponentOfType(children, "arg-index"), [children]);
  const [indexValue, setIndexValue] = useState(index);

  const usedIndex: number = useMemo(() => {
    if (mode === "relative") {
      return currentSearchHitIndex + parseInt(indexValue);
    }
    else {
      return parseInt(indexValue);
    }
  }, [indexValue, currentSearchHitIndex, mode]);

  // Use state and effect for setting disabled, so it's always true on the first render
  // This way SSR works without messing up the disabled attribute
  const [disabled, setDisabled] = useState<boolean>(true);
  useEffect(() => {
    // Only disable when index is out of bounds. Allow click when selected to scroll into view.
    setDisabled(usedIndex > searchHits.length - 1 || usedIndex < 0);
  }, [indexValue, currentSearchHitIndex, searchHits, usedIndex]);

  const clickHandler: React.MouseEventHandler<HTMLButtonElement | HTMLDivElement | HTMLSpanElement> = useCallback(() => {
    goToHit(usedIndex);
  }, [usedIndex, goToHit]);

  const button = useMemo(() => {
    const usedLabel = label || labelChild?.props.children;
    const activeStateClass = mode === "absolute"
      ? (usedIndex === currentSearchHitIndex ? "_active" : "_inactive")
      : undefined;
    const usedClassName = [
      className,
      disabled ? "_disabled" : "_enabled",
      activeStateClass,
    ].filter(Boolean).join(" ");
    const usedClickHandler = disabled ? undefined : clickHandler;
    switch (component) {
      case "button":
        return <button className={usedClassName} disabled={disabled} onClick={usedClickHandler}>{usedLabel}</button>
      case "span":
        return <span className={usedClassName} onClick={usedClickHandler}>{usedLabel}</span>
      case "div":
        return <div className={usedClassName} onClick={usedClickHandler}>{usedLabel}</div>
      default:
        return <></>;
    }
  }, [disabled, label, labelChild, clickHandler, component, className, mode, usedIndex, currentSearchHitIndex]);

  return <>
    {indexChild && (
      <TextExtractor onExtractedText={setIndexValue}>
        {indexChild?.props.children}
      </TextExtractor>
    )}
    {button}
  </>;
}